/**
 * ADR-0102 decision 3: a change to form values or adapted text after a
 * granskning was approved makes that approval stale. The editor asks once
 * per approval before the first such change.
 */

import { useCallback, useRef } from 'react';
import type { ContractApproval, ContractReview } from '../api.js';

export const APPROVAL_RESET_CONFIRM = 'Ändringen återställer godkännandet – avtalet behöver granskas igen.';

export const STALE_APPROVAL_NOTICE =
  'Godkännandet har återställts eftersom avtalet ändrades efter granskningen. Avtalet behöver granskas igen.';

/**
 * Whether a change now needs the confirm: the backend says it would reset
 * a current approval, and the user has not already accepted that for this
 * approval.
 */
export function needsApprovalResetConfirm(
  approval: Pick<ContractApproval, 'approvalWouldReset' | 'approvedReviewId'> | null | undefined,
  acknowledgedReviewId: string | null,
): boolean {
  if (!approval?.approvalWouldReset) return false;
  return approval.approvedReviewId == null || approval.approvedReviewId !== acknowledgedReviewId;
}

/** The latest closed review was an approval a later change made stale. */
export function approvalIsStale(reviews: ReadonlyArray<Pick<ContractReview, 'status'>>): boolean {
  const latestClosed = reviews.find((r) => r.status !== 'pending');
  return !reviews.some((r) => r.status === 'pending') && latestClosed?.status === 'superseded';
}

/**
 * `guard()` returns true when the change may go ahead: no current approval,
 * already confirmed for this approval, or the user confirms now.
 */
const browserConfirm = (message: string): boolean => window.confirm(message);

export function useApprovalResetGuard(
  approval: ContractApproval | null | undefined,
  confirmFn: (message: string) => boolean = browserConfirm,
): () => boolean {
  const acknowledged = useRef<string | null>(null);
  return useCallback(() => {
    if (!needsApprovalResetConfirm(approval, acknowledged.current)) return true;
    if (!confirmFn(APPROVAL_RESET_CONFIRM)) return false;
    acknowledged.current = approval?.approvedReviewId ?? null;
    return true;
  }, [approval, confirmFn]);
}
